import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { api } from '@/lib/teaching/teacher-api';
import { useAssessmentId, teachHref, invalidateDataset } from '@/lib/teaching/state';
import {
  errText, waitForJob, parseEvidence, IDENTITY_LABEL, paperPill, type Batch, type Job, type ScanPaper, type ScansView, type TrackerRow,
} from '@/lib/teaching/slice2';
import { Card } from '@/components/teaching/ui';
import { Btn, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { Pill, SetupPage, Tabs, Note, selectCls, NoAiNote } from '@/components/teaching/setup';
import { useAiReady, aiBlockText } from '@/lib/teaching/ai-ready';
import { PdfPages } from '@/components/teaching/PdfPages';

// Identity gate (§04, SOP §21): the AI reads name, scan number and code
// from each paper's first page; the server decides the status; the teacher
// confirms. Marks are only ever assigned to a confirmed paper.

export function Identity() {
  const a = useAssessmentId();
  const [view, setView] = useState<ScansView | null>(null);
  const [cls, setCls] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const load = async () => { if (a) setView(await api<ScansView>('GET', `/api/t/assessments/${a}/scans`)); };
  useEffect(() => { setView(null); void load().catch((e) => setErr(errText(e))); }, [a]);
  useEffect(() => { if (view && !view.tracker.some((t) => t.class_id === cls)) setCls(view.tracker[0]?.class_id ?? ''); }, [view]);
  const changed = async () => { if (a) invalidateDataset(a); await load(); };

  const klass = view?.tracker.find((t) => t.class_id === cls);
  const batches = view?.batches.filter((b) => b.class_id === cls) ?? [];
  return (
    <SetupPage a={a} title="Identity check" actions={a && <Link href={teachHref('/setup/review', { a })} class="h-9 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] text-[13px] grid place-items-center">Review queue</Link>}>
      <ErrorLine error={err} />
      {!view && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {view && klass && (
        <>
          {view.tracker.length > 1 && <Tabs label="Class" value={cls} onChange={setCls} options={view.tracker.map((t) => ({ id: t.class_id, label: t.name }))} />}
          {batches.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">No scans for {klass.name} yet. Upload a PDF on <Link href={teachHref('/setup/scans', { a })} class="text-[var(--color-accent)]">Scans and upload</Link> and each paper's name is checked here.</div></Card>}
          {batches.map((b) => <BatchIdentity key={b.id} b={b} papers={view.papers.filter((p) => p.batch_id === b.id)} roster={klass.rows} onChanged={changed} />)}
        </>
      )}
    </SetupPage>
  );
}

function BatchIdentity({ b, papers, roster, onChanged }: { b: Batch; papers: ScanPaper[]; roster: TrackerRow[]; onChanged: () => Promise<void> }) {
  const [job, setJob] = useState<Job | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toCheck = papers.filter((p) => p.mapping_status !== 'confirmed');
  const matchable = papers.filter((p) => p.mapping_status === 'proposed' && p.ai_identity_status === 'verified');
  const running = job && (job.status === 'queued' || job.status === 'running');
  const ai = useAiReady();

  async function runCheck() {
    setErr(null); setMsg(null);
    try {
      const r = await api<{ job: Job }>('POST', `/api/t/batches/${b.id}/identity`);
      setJob(r.job);
      const done = await waitForJob(r.job.id, setJob);
      if (done.status === 'failed') setErr(`Identity check failed: ${done.error ?? 'unknown error'}`);
      await onChanged();
    } catch (e) { setErr(errText(e)); setJob(null); }
  }
  async function confirmAll() {
    setErr(null);
    try {
      const r = await api<{ confirmed: string[] }>('POST', `/api/t/batches/${b.id}/identity/confirm-all`);
      setMsg(`Confirmed ${r.confirmed.length} paper${r.confirmed.length === 1 ? '' : 's'} where every identifier agreed.`);
      await onChanged();
    } catch (e) { setErr(errText(e)); }
  }

  return (
    <Card title={`${b.kind === 'main' ? 'Main' : 'Catch-up'} batch · ${papers.length} papers`} sub={`${papers.length - toCheck.length} of ${papers.length} confirmed`}
    >
      <div class="flex flex-wrap gap-2 mb-3">
        <Btn disabled={!!running || toCheck.length === 0 || ai !== true} title={ai === false ? aiBlockText() : undefined} onClick={runCheck}>{running ? 'Checking…' : 'Run identity check'}</Btn>
        <Btn kind="primary" disabled={matchable.length === 0} onClick={confirmAll}>Confirm all matches ({matchable.length})</Btn>
        {ai === false && toCheck.length > 0 && <span class="self-center"><NoAiNote /></span>}
      </div>
      {b.reconciliation.status !== 'reconciled' && <div class="mb-3"><Note tone="warn">This batch is not reconciled yet; marking stays blocked until it is. See Scans.</Note></div>}
      {msg && <div class="mb-3"><Note tone="ok">{msg}</Note></div>}
      <ErrorLine error={err} />
      <div class="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
        {papers.map((p) => <PaperCard key={`${p.id}:${p.mapping_revision}`} b={b} p={p} roster={roster} onChanged={onChanged} />)}
      </div>
    </Card>
  );
}

function PaperCard({ b, p, roster, onChanged }: { b: Batch; p: ScanPaper; roster: TrackerRow[]; onChanged: () => Promise<void> }) {
  const ev = parseEvidence(p.ai_identity_evidence);
  const st = p.ai_identity_status ? IDENTITY_LABEL[p.ai_identity_status] : null;
  const confirmed = p.mapping_status === 'confirmed';
  const eligible = roster.filter((r) => r.attendance !== 'absent' && r.attendance !== 'exempt');
  const [pick, setPick] = useState(p.student_id ?? '');
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState(!confirmed && !p.student_id);
  const [setPresent, setSetPresent] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [askPresent, setAskPresent] = useState(false);
  const [rev, setRev] = useState(p.mapping_revision);
  const chosen = roster.find((r) => r.student_id === pick);
  const remap = confirmed && pick !== p.student_id;

  async function confirm(studentId: string, present = chosen?.attendance === 'catchup' && setPresent) {
    setErr(null);
    try {
      const r = await api<{ needsAttendance: boolean; paper: { mapping_revision: number } }>('PUT', `/api/t/papers/${p.id}/identity`, {
        expectedMappingRevision: rev, studentId, ...(remap ? { reason: reason.trim() } : {}), ...(present ? { setPresent: true } : {}),
      });
      setRev(r.paper.mapping_revision);
      if (r.needsAttendance) { setAskPresent(true); return; }
      await onChanged();
    } catch (e) { setErr(errText(e)); }
  }

  return (
    <div class="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-3 flex gap-3 min-w-0" data-paper={p.scan_number}>
      <div class="shrink-0 w-[96px]">
        {p.first_page !== null && <PdfPages batchId={b.id} fileKey={b.file_sha256} first={p.first_page} last={p.first_page} width={96} />}
      </div>
      <div class="min-w-0 flex-1 flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-1.5">
          <span class="text-[15px] font-semibold text-[var(--color-text)]">{p.scan_number}</span>
          <Pill {...paperPill(p)} />
        </div>
        {st ? <div title={st.hint} class="flex flex-wrap items-center gap-1 text-[11px] text-[var(--color-text-faint)]">AI check <Pill label={st.label} bg={st.bg} /></div>
          : <div class="text-[11.5px] text-[var(--color-text-faint)]">Not checked by AI yet</div>}
        {ev?.read && (
          <dl class="grid grid-cols-[auto_1fr] gap-x-2 text-[11.5px]">
            <dt class="text-[var(--color-text-faint)]">Name</dt><dd class="text-[var(--color-text)] truncate">{ev.read.name_text ?? '-'}</dd>
            <dt class="text-[var(--color-text-faint)]">Number</dt><dd class="text-[var(--color-text)]">{ev.read.number_text ?? '-'}</dd>
            <dt class="text-[var(--color-text-faint)]">Code</dt><dd class="text-[var(--color-text)]">{ev.read.code_text ?? '-'}</dd>
          </dl>
        )}
        {ev?.detail && <div class="text-[11px] text-[var(--color-text-muted)]">{ev.detail}</div>}
        <div class="text-[12.5px] text-[var(--color-text)]">
          {confirmed ? 'Confirmed: ' : p.student_name ? 'Proposed: ' : ''}<b>{p.student_name ?? 'No student'}</b>
        </div>

        {!editing && !confirmed && p.student_id && (
          <div class="flex flex-wrap gap-2">
            <Btn kind="primary" onClick={() => confirm(p.student_id!)}>Confirm</Btn>
            <Btn onClick={() => setEditing(true)}>Pick another</Btn>
          </div>
        )}
        {!editing && confirmed && <div><Btn onClick={() => setEditing(true)}>Change student</Btn></div>}
        {editing && (
          <div class="flex flex-col gap-1.5">
            <select aria-label={`Student for paper ${p.scan_number}`} class={selectCls} value={pick} onChange={(e) => setPick(val(e))}>
              <option value="">Pick the student…</option>
              {eligible.map((r) => <option key={r.student_id} value={r.student_id}>{r.name}{r.student_code ? ` (${r.student_code})` : ''}{r.scan_number ? ` · ${r.scan_number}` : ''}{r.attendance === 'catchup' ? ' · catch-up' : ''}</option>)}
            </select>
            {remap && <input aria-label="Reason for the correction" class={inputCls} placeholder="Why the mapping is wrong" value={reason} onInput={(e) => setReason(val(e))} />}
            {chosen?.attendance === 'catchup' && (
              <label class="text-[12px] text-[var(--color-text)] flex items-center gap-1.5"><input type="checkbox" checked={setPresent} onChange={() => setSetPresent(!setPresent)} />Sat the catch-up: set present</label>
            )}
            {remap && <div class="text-[11.5px] text-[#b42318]">Approved marks from this paper are voided (kept in the log).</div>}
            <div class="flex gap-2">
              <Btn kind="primary" disabled={!pick || (remap && !reason.trim())} onClick={() => confirm(pick)}>{remap ? 'Remap' : 'Confirm'}</Btn>
              {(confirmed || p.student_id) && <Btn onClick={() => { setEditing(false); setPick(p.student_id ?? ''); }}>Cancel</Btn>}
            </div>
          </div>
        )}
        {askPresent && (
          <Note tone="warn">
            {chosen?.name ?? 'This student'} is marked catch-up. Their marks cannot be approved until they are present.
            <div class="mt-1.5 flex gap-2"><Btn kind="primary" onClick={() => { setAskPresent(false); void confirm(pick || p.student_id!, true); }}>Set present</Btn><Btn onClick={() => { setAskPresent(false); void onChanged(); }}>Later</Btn></div>
          </Note>
        )}
        <ErrorLine error={err} />
      </div>
    </div>
  );
}
